var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useEffect, useMemo } from "react";
import { WarnBox, EWarnBoxAnnouncement } from "@sequentech/ui-essentials";
import { provideBallotService } from "../../services/BallotService";
import { useAppSelector } from "../../store/hooks";
import { useTranslation } from "react-i18next";
import { EBlankVotePolicy, EUnderVotePolicy, EInvalidVotePolicy, EOverVotePolicy, translateHtml, } from "@sequentech/ui-core";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import { isVotedByElectionId } from "../../store/extra/extraSlice";
import { useParams } from "react-router-dom";
import { IInvalidPlaintextErrorType } from "../../types/errors";
var ErrorWrapper = styled(Box)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    gap: 4px;\n    margin-bottom: 12px;\n"], ["\n    display: flex;\n    flex-direction: column;\n    gap: 4px;\n    margin-bottom: 12px;\n"
    // The write-in length error is the one message tied to a specific control, so it
    // gets a predictable id that the write-in field can point aria-describedby at.
])));
// The write-in length error is the one message tied to a specific control, so it
// gets a predictable id that the write-in field can point aria-describedby at.
export var writeInErrorId = function (contestId) { return "contest-".concat(contestId, "-writein-error"); };
// The contest's answer group points aria-describedby here, so the reason the
// voter cannot continue is read out along with the group.
export var contestErrorsId = function (contestId) { return "contest-".concat(contestId, "-errors"); };
export var InvalidErrorsList = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var ballotStyle = _a.ballotStyle, question = _a.question, hasWriteIns = _a.hasWriteIns, isInvalidWriteIns = _a.isInvalidWriteIns, setIsInvalidWriteIns = _a.setIsInvalidWriteIns, setDecodedContests = _a.setDecodedContests, isReview = _a.isReview, errorSelectionState = _a.errorSelectionState, isTouched = _a.isTouched, setIsTouched = _a.setIsTouched;
    var t = useTranslation().t;
    // Note that if we have reviewed, then we can asume we have touched
    var electionId = useParams().electionId;
    var isVotedState = useAppSelector(isVotedByElectionId(electionId));
    var _k = provideBallotService(), interpretContestSelection = _k.interpretContestSelection, interpretMultiContestSelection = _k.interpretMultiContestSelection, getWriteInAvailableCharacters = _k.getWriteInAvailableCharacters;
    var under_vote_policy = (_c = (_b = question === null || question === void 0 ? void 0 : question.presentation) === null || _b === void 0 ? void 0 : _b.under_vote_policy) !== null && _c !== void 0 ? _c : undefined;
    var blank_vote_policy = (_e = (_d = question === null || question === void 0 ? void 0 : question.presentation) === null || _d === void 0 ? void 0 : _d.blank_vote_policy) !== null && _e !== void 0 ? _e : undefined;
    var invalid_vote_policy = (_g = (_f = question === null || question === void 0 ? void 0 : question.presentation) === null || _f === void 0 ? void 0 : _f.invalid_vote_policy) !== null && _g !== void 0 ? _g : undefined;
    var over_vote_policy = (_j = (_h = question === null || question === void 0 ? void 0 : question.presentation) === null || _h === void 0 ? void 0 : _h.over_vote_policy) !== null && _j !== void 0 ? _j : undefined;
    var decodedContestSelection = errorSelectionState.find(function (selection) { return selection.contest_id === question.id; });
    var containsError = function (state, message) {
        if (!state)
            return false;
        return (state.invalid_alerts.find(function (error) { return error.message === message; }) ||
            state.invalid_errors.find(function (error) { return error.message === message; }));
    };
    var filterErrorList = function (state, isTouched, isVotedState, isReview, under_vote_policy, blank_vote_policy, invalid_vote_policy, over_vote_policy) {
        if (!state)
            return undefined;
        var ret = __assign(__assign({}, state), { invalid_alerts: (state === null || state === void 0 ? void 0 : state.invalid_alerts.filter(
            // !() is used so that function instead of behaving like
            // "show error when this happens" behaves more like "hide
            // error when this happens"
            function (error) {
                return !(("errors.implicit.underVote" === error.message &&
                    !isReview &&
                    under_vote_policy === EUnderVotePolicy.WARN_ONLY_IN_REVIEW) ||
                    ("errors.implicit.blankVote" === error.message &&
                        !isReview &&
                        blank_vote_policy === EBlankVotePolicy.WARN_ONLY_IN_REVIEW) ||
                    (error.message === "errors.implicit.overVoteDisabled" && isReview));
            })) || [] });
        if (!isReview && !isTouched) {
            ret.invalid_alerts = [];
            ret.invalid_errors = [];
        }
        // remove duplicates
        ret.invalid_alerts = ret.invalid_alerts.filter(function (error) {
            return !(
            // if there's blank vote, remove underVote
            (("errors.implicit.underVote" === error.message &&
                containsError(ret, "errors.implicit.blankVote")) ||
                // if overvote is an error, remove the info message
                ("errors.implicit.selectedMax" === error.message &&
                    containsError(ret, "errors.implicit.selectedMax"))));
        });
        ret.invalid_errors = ret.invalid_errors.filter(function (error) {
            var ret = !((invalid_vote_policy === EInvalidVotePolicy.ALLOWED ||
                invalid_vote_policy === EInvalidVotePolicy.ALLOWED_WITH_EXCLUSIVE_EXPLICIT) &&
                !("errors.implicit.selectedMax" === error.message &&
                    over_vote_policy !== EOverVotePolicy.ALLOWED) &&
                !("errors.implicit.blankVote" === error.message &&
                    blank_vote_policy === EBlankVotePolicy.NOT_ALLOWED));
            return ret;
        });
        return ret;
    };
    var filteredSelection = useMemo(function () {
        return filterErrorList(decodedContestSelection, isTouched, isVotedState, isReview, under_vote_policy, blank_vote_policy, invalid_vote_policy, over_vote_policy);
    }, [
        decodedContestSelection,
        isTouched,
        isVotedState,
        isReview,
        under_vote_policy,
        blank_vote_policy,
    ]);
    useEffect(function () {
        if (decodedContestSelection) {
            setDecodedContests(decodedContestSelection);
        }
    }, [decodedContestSelection]);
    useEffect(function () {
        if (isTouched || !decodedContestSelection) {
            return;
        }
        var hasTouched = decodedContestSelection === null || decodedContestSelection === void 0 ? void 0 : decodedContestSelection.choices.some(function (choice) { return choice.selected > -1; });
        if (hasTouched) {
            setIsTouched(true);
        }
    }, [decodedContestSelection, isTouched]);
    var numAvailableChars = hasWriteIns && decodedContestSelection
        ? getWriteInAvailableCharacters(decodedContestSelection, ballotStyle.ballot_eml)
        : 0;
    useEffect(function () {
        var newInvalid = numAvailableChars < 0;
        if (newInvalid !== isInvalidWriteIns) {
            setIsInvalidWriteIns(newInvalid);
        }
    }, [numAvailableChars, isInvalidWriteIns, setIsInvalidWriteIns]);
    return (_jsxs(ErrorWrapper, { className: "error-list", id: contestErrorsId(question.id), role: "status", children: [numAvailableChars < 0 ? (_jsx(WarnBox, { className: "write-in-error", variant: "warning", id: writeInErrorId(question.id), 
                // The write-in field points aria-describedby at this box, so
                // announcing it as well would read the same text twice.
                announcement: EWarnBoxAnnouncement.SILENT, warnId: "errors.encoding.writeInCharsExceeded", warnType: IInvalidPlaintextErrorType.EncodingError, children: translateHtml(t, "errors.encoding.writeInCharsExceeded", {
                    numCharsExceeded: -numAvailableChars,
                }) })) : null, filteredSelection === null || filteredSelection === void 0 ? void 0 : filteredSelection.invalid_errors.map(function (error, index) {
                var _a;
                return (_jsx(WarnBox, { className: "contest-validation-warning", variant: "warning", announcement: EWarnBoxAnnouncement.SILENT, warnId: error.message, warnType: error.error_type, children: translateHtml(t, error.message || "", (_a = error.message_map) !== null && _a !== void 0 ? _a : {}) }, index));
            }), filteredSelection === null || filteredSelection === void 0 ? void 0 : filteredSelection.invalid_alerts.map(function (error, index) {
                var _a;
                return (_jsx(WarnBox, { className: "contest-validation-info", variant: "info", announcement: EWarnBoxAnnouncement.SILENT, warnId: error.message, warnType: error.error_type, children: translateHtml(t, error.message || "", (_a = error.message_map) !== null && _a !== void 0 ? _a : {}) }, index));
            })] }));
};
var templateObject_1;
